/* Dar de alta un chofer en documentación.
 *
 * ── POR QUÉ HACE FALTA ────────────────────────────────────────────────────
 *
 * Hasta hoy la única forma de que alguien apareciera era escribirlo en la
 * planilla del Drive y esperar al sync de las 03:50. Un chofer que entra hoy no
 * existía para el sistema hasta mañana — y si nadie tocaba la planilla, nunca.
 *
 * ── BLOQUEAR Y AVISAR NO SON LO MISMO, Y ESO ORDENA EL MÓDULO ────────────
 *
 * Es la misma distinción de `shared/duplicados.js`:
 *
 *   · **choca con el DNI** → se BLOQUEA. El DNI es la clave: guardar ahí pisa
 *     una fila que ya existe, y eso no es un aviso, es una pérdida de datos.
 *   · **cualquier otra cosa rara** → se AVISA y decide la persona.
 *
 * ── NO ESTAR EN EL PADRÓN NO ES UN ERROR ─────────────────────────────────
 *
 * Hay **13 DNI** en documentación que el padrón no tiene: gente con papeles
 * cargados y sin fila en `personas`. Tratarlo como un error obligaría a
 * inventar una fila del padrón para poder cargar un vencimiento, que es
 * exactamente al revés de para lo que sirve esta pantalla. Se dice, y se sigue.
 *
 * Al revés sí sirve: **si el DNI está en el padrón, el nombre se ofrece**. Es
 * el cotejo del alta de movimientos aplicado acá — el nombre ya está escrito en
 * algún lado y tipearlo de nuevo es la forma de que difieran.
 */

/* Los DNI reales: 7 u 8 dígitos. En el padrón hay 64 personas con 7 —los más
 * grandes— así que exigir 8 los dejaría afuera. */
const LARGO_OK = [7, 8];

export const soloDigitos = (v) => String(v ?? "").replace(/\D/g, "");

/* `{ ok, errores, avisos }`. Los errores frenan; los avisos se muestran al
 * lado del botón y se guarda igual. */
export function revisar({ dni, nombre }, { yaExiste = false, enPadron = null } = {}) {
  const errores = [];
  const avisos = [];

  const d = soloDigitos(dni);
  if (!d) {
    errores.push("Falta el DNI.");
  } else if (yaExiste) {
    /* LO ÚNICO QUE FRENA DE VERDAD. Guardar sobre un DNI que ya está pisa esa
     * fila con lo que haya en el formulario — se perderían los vencimientos
     * cargados. Si hay que corregir a alguien que ya está, se corrige en su
     * fila. */
    errores.push(
      "Ya hay un chofer con ese DNI. Para corregirlo, buscalo en la lista.",
    );
  } else if (!LARGO_OK.includes(d.length)) {
    // Once dígitos es un CUIT pegado en el campo equivocado, y es el error
    // que más se comete. No se bloquea: se nombra, que es lo que permite
    // darse cuenta.
    avisos.push(
      d.length === 11
        ? "Eso parece un CUIT y no un DNI (once dígitos)."
        : `Un DNI tiene 7 u 8 dígitos y ése tiene ${d.length}.`,
    );
  }

  if (!String(nombre ?? "").trim()) {
    /* Sin nombre la fila es un número suelto: la lista lo dibuja como «sin
     * nombre cargado» y nadie sabe a quién llamar. Es el único otro campo que
     * frena. */
    errores.push("Falta el nombre.");
  }

  /* `null` es «todavía no se miró» y no se dice nada. `false` es «se miró y no
   * está», que son los 13 casos reales: se avisa y se sigue. */
  if (enPadron === false && d) {
    avisos.push(
      "Ese DNI no está en el padrón de gente. No es un error — hay 13 así.",
    );
  }

  return { ok: errores.length === 0, errores, avisos };
}

/* Lo que se manda a guardar. Los vacíos van como `null` y no como `""`:
 * `evaluar` distingue «sin fecha» de una fecha, y una cadena vacía en una
 * columna `date` la rechaza PostgREST — la fila entera, como siempre. */
export function aFila({ dni, nombre, empresa, telefono, zona, registro_vence,
                        tipo_servicio }) {
  const limpio = (v) => {
    const s = String(v ?? "").trim();
    return s || null;
  };
  return {
    dni: soloDigitos(dni),
    nombre: limpio(nombre),
    empresa: limpio(empresa),
    telefono: limpio(telefono),
    zona: limpio(zona),
    registro_vence: limpio(registro_vence),
    tipo_servicio: limpio(tipo_servicio),
  };
}
